import { graphql, commitMutation, Environment } from 'react-relay';
import debounce from 'lodash.debounce';
import { trackEvent } from 'dibs-tracking';

import { getUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { REST_OF_WORLD_REGION } from '../helpers/shippingCostHelpers';
import StaticShippingBadgeStyles from './StaticShippingBadge.scss';
import StaticTileHeartStyles from './StaticTileHeart.scss';

import { staticTileHelpersModifyPortfolioItemsMutation } from './__generated__/staticTileHelpersModifyPortfolioItemsMutation.graphql';
import QuickViewItemStyles from '../QuickViewItem/styles.scss';
import SwipeIndicatorStyles from '../SwipeIndicator/swipeIndicator.scss';

export type Currencies = 'usd' | 'gbp' | 'eur' | 'cad' | 'aud' | 'chf' | 'mxn';
export type CurrencyAttributes = `data-price-${Currencies}`;

export const ATTRIBUTE_STATIC_TILE = 'data-static-tile';
export const ATTRIBUTE_STATIC_PRICE = 'data-static-price';
export const ATTRIBUTE_SHOW_STATIC_PRICE = 'data-show-price';
const ATTRIBUTE_SHOW_STATIC_MEASUREMENTS = 'data-show-measurements';
export const ATTRIBUTE_TRACKING_STATIC_PRODUCT_CAROUSEL = 'data-static-product-carousel';
export const ATTRIBUTE_STATIC_CAROUSEL_SWIPE_INDICATOR = 'data-static-carousel-swipe';
export const ATTRIBUTE_SHIPPING = 'data-regions';
export const ATTRIBUTE_HEART_ITEM_ID = 'data-static-favorite-id';
const ATTRIBUTE_SHOW_HEARTS = 'data-show-favorites';
export const CLASS_NAME_QUICK_VIEW_CONTAINER = 'qv-container';
export const CLASS_NAME_QUICK_VIEW_CLOSE_BUTTON = 'qv-close';
export const CLASS_NAME_QUICK_VIEW_OPEN_BUTTON = 'qv-open';

/**
 * If this is applied - the item does not have free shipping.
 */
export const CLASS_NAME_NO_FREE_SHIPPING = StaticShippingBadgeStyles.isInvisible;
/**
 * We're intentionally allowing just one instance.
 */
let previousCurrency: string | undefined;

export function displayPrices({
    currency,
    showCurrency,
}: {
    currency: Currencies;
    showCurrency: boolean;
}): void {
    if (currency && currency !== previousCurrency && showCurrency) {
        document.querySelectorAll(`[${ATTRIBUTE_STATIC_PRICE}]`).forEach(element => {
            const currencyToSearch = currency.toLowerCase() as Currencies;
            const attributeName: CurrencyAttributes = `data-price-${currencyToSearch}` as const;
            const newAmount = element.getAttribute(attributeName);
            if (newAmount) {
                element.textContent = newAmount;
            }
        });
        previousCurrency = currency;
        /**
         * After we insert prices into the DOM, show them! A class in `StaticPrice.scss` should
         * show the prices based on this attribute. The value should not matter.
         */
        document.body.setAttribute(ATTRIBUTE_SHOW_STATIC_PRICE, 'true');
    }
}

export function displayMeasurements({
    showMeasurements,
    measurementsUnit,
}: {
    showMeasurements: boolean;
    measurementsUnit: string | null;
}): void {
    if (showMeasurements && measurementsUnit) {
        document.querySelectorAll(`[data-static-measurements]`).forEach(element => {
            const attributeName = `data-measurement-${measurementsUnit.toLowerCase()}`;
            const measurementValue = element.getAttribute(attributeName);

            if (measurementValue) {
                element.textContent = measurementValue;
            }
        });

        document.body.setAttribute(ATTRIBUTE_SHOW_STATIC_MEASUREMENTS, 'true');
    }
}

export function displayFreeShipping(): void {
    const userRegions = (getUserSessionRegions() || []).map(region => region.displayName);
    document.querySelectorAll(`[${ATTRIBUTE_SHIPPING}]`).forEach(element => {
        const itemRegions = element.getAttribute(ATTRIBUTE_SHIPPING)?.split(',') || [];
        if (
            itemRegions.some(itemRegion => userRegions.includes(itemRegion)) ||
            itemRegions.includes(REST_OF_WORLD_REGION)
        ) {
            element.classList.remove(StaticShippingBadgeStyles.isInvisible);
        }
    });
}

export function displayFavorites(favoriteIds: string[]): void {
    document.querySelectorAll(`[${ATTRIBUTE_HEART_ITEM_ID}]`).forEach(element => {
        const itemId = element.getAttribute(ATTRIBUTE_HEART_ITEM_ID) || '';

        if (favoriteIds.includes(itemId)) {
            element.classList.add(StaticTileHeartStyles.fill);
        }
    });
    setTimeout(() => {
        // wait color transition end to avoid flicker
        document.body.setAttribute(ATTRIBUTE_SHOW_HEARTS, 'true');
    }, 201);
}

export function addFavoritesClickHandler({
    environment,
    userId,
    onFavorite,
    triggerLoginModal,
}: {
    environment: Environment;
    userId: string;
    onFavorite: ({ isFavorited, itemId }: { isFavorited: boolean; itemId: string }) => void;
    triggerLoginModal: () => Promise<{ userId: string }>;
}): () => void {
    const isLoggedIn = !!userId;
    const handler = debounce(
        async (event: MouseEvent | KeyboardEvent): Promise<void> => {
            const element = (event?.target as Element)?.closest(`[${ATTRIBUTE_HEART_ITEM_ID}]`);

            if (element !== null && event) {
                const itemId = element.getAttribute(ATTRIBUTE_HEART_ITEM_ID) || '';
                const isFilled = element.classList.contains(StaticTileHeartStyles.fill);

                const addOrRemoveFromUserFavorites = (loggedInUserId: string): void => {
                    if (isFilled) {
                        element.classList.remove(StaticTileHeartStyles.fill);
                    } else {
                        element.classList.add(StaticTileHeartStyles.fill);
                    }
                    onFavorite({ isFavorited: !isFilled, itemId });
                    commitMutation<staticTileHelpersModifyPortfolioItemsMutation>(environment, {
                        mutation: graphql`
                            mutation staticTileHelpersModifyPortfolioItemsMutation(
                                $input: ModifyPortfolioItemsInput!
                            ) {
                                modifyPortfolioItems(input: $input) {
                                    __typename
                                }
                            }
                        `,
                        variables: {
                            input: {
                                userId: loggedInUserId,
                                itemsIds: [itemId],
                                action: isFilled ? 'REMOVE' : 'ADD',
                                portfolioType: 'HEART',
                            },
                        },
                    });
                };

                if (!isLoggedIn) {
                    const { userId: loggedInUserId } = await triggerLoginModal();
                    if (loggedInUserId) {
                        addOrRemoveFromUserFavorites(loggedInUserId);
                    }
                } else {
                    addOrRemoveFromUserFavorites(userId);
                }
            }
        },
        360, // protect against rage clicks
        { leading: true }
    );

    document.body.addEventListener('click', handler);

    return () => document.body.removeEventListener('click', handler);
}

export function handleQuickView(): void {
    document.querySelectorAll(`[${ATTRIBUTE_STATIC_TILE}]`).forEach(tileElement => {
        tileElement.addEventListener('click', e => {
            const targetElement = e.target;
            if (targetElement instanceof Element) {
                const isQuickViewOpen = targetElement.closest(
                    `.${CLASS_NAME_QUICK_VIEW_OPEN_BUTTON}`
                );
                const isQuickViewClose = targetElement.closest(
                    `.${CLASS_NAME_QUICK_VIEW_CLOSE_BUTTON}`
                );
                const quickViewContainer = tileElement.querySelector(
                    `.${CLASS_NAME_QUICK_VIEW_CONTAINER}`
                );

                if (!quickViewContainer) {
                    return;
                }

                if (isQuickViewOpen) {
                    quickViewContainer.classList.add(QuickViewItemStyles.isActive);
                } else if (isQuickViewClose) {
                    quickViewContainer.classList.remove(QuickViewItemStyles.isActive);
                }
            }
        });
    });
}

export function handleStaticCarouselTracking(): void {
    document
        .querySelectorAll(`[${ATTRIBUTE_TRACKING_STATIC_PRODUCT_CAROUSEL}]`)
        .forEach(containerElement => {
            const itemId = containerElement.getAttribute(
                ATTRIBUTE_TRACKING_STATIC_PRODUCT_CAROUSEL
            );
            let isListeningForTouchEnd = false;
            function handleTouchEnd(): void {
                trackEvent({
                    additional: { itemId },
                    category: 'results',
                    isInteractiveEvent: true,
                    action: 'results page image carousel arrow click',
                });
                containerElement.removeEventListener('touchend', handleTouchEnd);
                isListeningForTouchEnd = false;
            }
            /**
             * Attempt to figure out when the user is interacting with the carousel.
             *
             * Listening just for the scroll event is an issue when the user stops the scroll, but
             * doesn't lift the finger. That way the scroll tops, we fire the event, the user lifts up
             * the finger and the carousel snaps into place, triggering the scroll event again - hence
             * firing tracking twice.
             *
             * Listiening just for the touchend event isn't good too, since it will fire on click.
             *
             * So we try to combine both of them. There's just on problem - if scroll is not triggered
             * by a touch event, this might not work correctly. But can that happen on a mobile device?
             */
            containerElement.addEventListener('scroll', () => {
                if (isListeningForTouchEnd) {
                    return;
                }

                isListeningForTouchEnd = true;
                containerElement.addEventListener('touchend', handleTouchEnd);
            });
        });
}

export function handleStaticCarouselSwipeIndicator(): void {
    const carouselContainer = document.querySelector(
        `[${ATTRIBUTE_TRACKING_STATIC_PRODUCT_CAROUSEL}]`
    );
    if (!carouselContainer) {
        return;
    }

    const swipeIndicator = carouselContainer.querySelector(
        `[${ATTRIBUTE_STATIC_CAROUSEL_SWIPE_INDICATOR}]`
    );

    if (!swipeIndicator) {
        return;
    }

    function handleScroll(): void {
        swipeIndicator?.classList.remove(SwipeIndicatorStyles.isVisible);
        carouselContainer?.removeEventListener('scroll', handleScroll);
    }
    carouselContainer.addEventListener('scroll', handleScroll);
}
